import React, {useCallback, useEffect, useState} from "react";
import {useParams} from "react-router-dom";
import {Button, H1, Text, styled} from "@reside/ui";

import {Spinner} from "../../atoms/spinner";
import {generateRatesPdf} from "../../services/AdmissionsService";

/**
 * Generates the Basic Rates PDF for an admission and downloads it. Opened in a new tab
 * from the rates-document link in the admission content (see toRatesDocumentUrl). The
 * presigned URL is served as an attachment, so the tab stays here once it's navigated to.
 */
export const PageRatesDocument = () => {
  const {admissionId} = useParams<{admissionId: string}>();
  const [failed, setFailed] = useState(false);
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null);

  const generate = useCallback(async () => {
    setFailed(false);
    setDownloadUrl(null);
    try {
      const url = await generateRatesPdf(admissionId!);
      setDownloadUrl(url);
      window.location.replace(url);
    } catch (error) {
      setFailed(true);
    }
  }, [admissionId]);

  useEffect(() => {
    generate();
  }, [generate]);

  return (
    <Page>
      {failed ? (
        <>
          <H1 fontSize={[3, 5]}>We couldn't prepare the rates document</H1>
          <Text as="p" fontSize={[3, 4]}>
            Please try again, or ask your Facility Representative for a copy.
          </Text>
          <Button color="primary" onClick={generate}>
            Try again
          </Button>
        </>
      ) : downloadUrl ? (
        <>
          <H1 fontSize={[3, 5]}>Your rates document has been downloaded</H1>
          <Text as="p" fontSize={[3, 4]}>
            You can close this tab, or{" "}
            <a href={downloadUrl}>download it again</a>.
          </Text>
        </>
      ) : (
        <>
          <Spinner relative />
          <H1 fontSize={[3, 5]}>Preparing the rates document...</H1>
          <Text as="p" fontSize={[3, 4]}>
            This may take up to a minute. Thank you for your patience.
          </Text>
        </>
      )}
    </Page>
  );
};

const Page = styled.div`
  padding: 80px;
  text-align: center;
`;
